// ── Enums ──────────────────────────────────────────────────────────────────────
export type TicketStatus = 'open' | 'accepted' | 'rejected' | 'early_settled' | 'settled' | 'cashed_out' | 'correction_pending' | 'cancelled';
export type TicketType = 'single' | 'acca' | 'system';
export type UserRole = 'user' | 'trader' | 'admin' | 'support' | 'finance';

// ── Auth ──────────────────────────────────────────────────────────────────────
export interface LoginIn {
  identity: string;
  password: string;
}

export interface RegisterIn {
  email: string;
  username: string;
  password: string;
  currency?: string;
  country_code?: string;
}

export interface TokenPairOut {
  access_token: string;
  refresh_token: string;
  token_type?: string; // default: "bearer"
}

export interface UserOut {
  id: string;
  email: string;
  username: string;
  role: UserRole;
  currency: string;
  country_code?: string | null;
  kyc_status: string;
  rg_status: string;
}

export interface AuthBundleOut {
  user: UserOut;
  tokens: TokenPairOut;
}

export interface RegistrationCurrency {
  currency: string;
  is_default: boolean;
}

export interface RegistrationOptionsOut {
  currencies: RegistrationCurrency[];
  currency_required: boolean;
}

// Catalog
export interface SportItem {
  sport_id: string;
  sport_name: string;
  event_count: number;
}

export interface Competitor {
  id?: string | null;
  name?: string | null;
  crest_url?: string | null;
}

export interface EventStatus {
  home_score: number;
  away_score: number;
  clock?: string;
  match_status_text?: string;
  status_text?: string;
  stoppage_time?: string;
  period_scores?: Array<{
    number: number;
    home_score: number;
    away_score: number;
    match_status_code: string;
  }>;
  statistics?: {
    yellow_cards?: { home: number; away: number };
    red_cards?: { home: number; away: number };
    corners?: { home: number; away: number };
  };
}

export interface Outcome {
  outcome_id: string;
  name: string;
  odds: string;
  probability: string;
  active: boolean;
}

export interface Market {
  market_id: string;
  canonical_key: string;
  status: number;
  favourite: boolean;
  name: string;
  specifiers?: Record<string, string>;
  market_metadata?: {
    start_time?: string;
    end_time?: string;
    next_betstop?: string;
    aams_id?: string;
  };
  outcomes: Outcome[];
}

export interface EventData {
  event_kind?: string;
  name?: string;
  sport_id: string;
  sport_name: string;
  category_id: string;
  category_name: string;
  country_code?: string;
  tournament_id: string;
  tournament_name: string;
  season_id?: string;
  season_name?: string;
  scheduled_at: string;
  start_time: string;
  home?: Competitor;
  away?: Competitor;
}

export interface EventSnapshot {
  event_id: string;
  scope: 'live' | 'pre' | 'outright';
  version: number;
  event: EventData;
  status: EventStatus;
  markets: Market[];
}

export interface CatalogHomeOut {
  live: EventSnapshot[];
  pre: EventSnapshot[];
}

export interface CatalogCollectionOut {
  items: EventSnapshot[];
}

export interface TournamentItem {
  tournament_id: string;
  tournament_name: string;
  category_id?: string;
  category_name?: string;
  country_code?: string;
  sport_id?: string;
  sport_name?: string;
  event_count: number;
}

export interface TournamentsCollectionOut {
  items: TournamentItem[];
}

export interface SportsCollectionOut {
  items: SportItem[];
}

export interface CategoryItem {
  sport_id: string;
  sport_name: string;
  category_id: string;
  category_name: string;
  country_code: string;
  event_count: number;
}

export interface CategoriesCollectionOut {
  items: CategoryItem[];
  locale?: string;
  fallback_locale?: string;
}

export interface PrimaryMarket {
  market_key: string;
  market_id: string;
  canonical_key: string;
  specifiers?: Record<string, string>;
  outcomes: Outcome[];
}

export interface TopEventItem {
  rank: number;
  event_id: string;
  event: EventData;
  primary_market: PrimaryMarket;
  more_bets_count: number;
}

export interface TopEventsCollectionOut {
  locale: string;
  fallback_locale: string;
  scope: string;
  view: string;
  selected_sport_id: string;
  selected_market_key: string;
  sport_tabs: any[]; // define as needed
  market_tabs: any[];
  items: TopEventItem[];
  pagination: {
    limit: number;
    offset: number;
    has_more: boolean;
  };
}

// ── Bets ──────────────────────────────────────────────────────────────────────
export interface SelectionIn {
  event_id: string;
  market_id: string;
  specifiers?: string;
  outcome_id: string;
}

export interface SystemBetIn {
  combination_sizes: number[];
}

export interface EventGroupSelectionIn {
  event_id: string;
  operator: 'any' | 'all';
  pricing?: 'provider_custom_bet' | string;
  selections: Omit<SelectionIn, 'event_id'>[];
  accepted_odds?: string;
}

export interface QuoteIn {
  ticket_type: TicketType;
  stake: number | string;
  selections?: SelectionIn[];
  event_groups?: EventGroupSelectionIn[];
  system?: SystemBetIn;
}

export interface SelectionQuoteOut {
  event_id: string;
  market_id: string;
  specifiers_norm: string;
  outcome_id: string;
  source_product_id: number | null;
  source_version: number | null;
  accepted_odds: string;
  accepted_probability: string | null;
  outcome_name: string | null;
  event_name: string;
}

export interface QuoteOut {
  ticket_type: TicketType;
  selections: SelectionQuoteOut[];
  acca_mode?: 'standard' | 'multi_way';
  multi_way_mode?: 'exclusive_paths' | 'pay_all_winning_lines' | 'provider_custom_bet';
  event_groups?: any[]; // To match OpenAPI AccumulatorEventGroupOut
  system?: any; // To match OpenAPI SystemDefinitionOut
  stake_basis?: string;
  number_of_bets?: number;
  effective_stake_per_bet?: string;
  min_line_odds?: string;
  max_line_odds?: string;
  minimum_line_payout?: string;
  maximum_line_payout?: string;
  all_lines_win_payout?: string;
  all_lines_win_return_is_achievable?: boolean;
  potential_payout_is_exact?: boolean;
  accepted_total_odds: string;
  potential_payout: string;
  expires_at: string;
  lines?: any[]; // TicketLineOut
}

export interface PlaceBetIn {
  ticket_type: TicketType;
  stake: number | string;
  selections?: SelectionIn[];
  event_groups?: EventGroupSelectionIn[];
  system?: SystemBetIn;
  idempotency_key?: string | null;
  use_bonus_balance?: boolean;
  odds_change_policy?: 'reject' | 'accept_better' | 'accept_any';
}

export interface TicketLegOut {
  id: string;
  leg_index: number;
  event_id: string;
  product_id: number | null;
  market_id: string;
  specifiers_norm: string;
  canonical_market_key: string;
  outcome_id: string;
  outcome_name: string | null;
  accepted_odds: string;
  accepted_probability: string | null;
  source_version: number | null;
  result_status: string;
  provider_result_code: number | null;
  void_factor: string | null;
  dead_heat_factor: string | null;
  settlement_reason: string | null;
  settled_at: string | null;
  current_team_scores?: { home_score: number; away_score: number };
  placement_team_scores?: { home_score: number; away_score: number };
  current_period_scores?: Array<{ number: number; home_score: number; away_score: number; match_status_code: string }>;
  placement_period_scores?: Array<{ number: number; home_score: number; away_score: number; match_status_code: string }>;
  // Not in OpenAPI spec but may be returned by backend:
  event_name?: string | null;
  market_name?: string | null;
  event?: TicketLegEventOut | null;
}

export interface TicketLegEventOut {
  id?: string;
  category_name?: string | null;
  tournament_name?: string | null;
  home?: TicketCompetitorOut | null;
  away?: TicketCompetitorOut | null;
}

export interface TicketCompetitorOut {
  id?: string | null;
  name?: string | null;
}

export interface TicketEventOut {
  event_id: string;
  event_name?: string | null;
  event?: TicketLegEventOut | null;
  current_state?: {
    team_scores?: { home_score: number; away_score: number } | null;
    period_scores?: Array<{ number: number; home_score: number; away_score: number; match_status_code: string }> | null;
  } | null;
  placement_state?: {
    team_scores?: { home_score: number; away_score: number } | null;
    period_scores?: Array<{ number: number; home_score: number; away_score: number; match_status_code: string }> | null;
  } | null;
}

export interface TicketLineOut {
  line_index: number;
  fold_size: number;
  effective_stake: string;
  accepted_total_odds: string;
  projected_payout: string;
  result_status: string;
  settled_payout?: string | null;
}

export interface TicketOut {
  id: string;
  ticket_reference?: string | null;
  user_id: string;
  ticket_type: TicketType;
  acca_mode?: 'standard' | 'multi_way' | null;
  multi_way_mode?: 'exclusive_paths' | 'pay_all_winning_lines' | 'provider_custom_bet' | null;
  event_groups?: any[];
  system?: any;
  status: TicketStatus;
  currency: string;
  stake_cash: string;
  stake_bonus: string;
  total_stake: string;
  stake_basis?: string;
  number_of_bets?: number;
  effective_stake_per_bet?: string;
  min_line_odds?: string;
  max_line_odds?: string;
  minimum_line_payout?: string;
  maximum_line_payout?: string;
  all_lines_win_payout?: string;
  all_lines_win_return_is_achievable?: boolean | null;
  potential_payout_is_exact?: boolean;
  accepted_total_odds: string;
  potential_payout: string;
  financial_payout_posted: string;
  idempotency_key: string | null;
  rejection_reason: string | null;
  created_at: string;
  settled_at: string | null;
  metadata_json?: Record<string, unknown>;
  events?: TicketEventOut[];
  legs: TicketLegOut[];
  lines?: TicketLineOut[];
}

// Wallet
export interface WalletOut {
  user_id: string;
  currency: string;
  available_cash: string;
  locked_cash: string;
  available_bonus: string;
  locked_bonus: string;
  version: number;
}

export interface CustomerCurrency {
  currency: string;
  minor_units: number;
  is_default: boolean;
}

export interface CustomerAccountOut {
  person_id: string;
  account_id: string;
  account_reference: string;
  account_type: string;
  status: string;
  owner_node_id: string;
  currencies: CustomerCurrency[];
  version: number;
}

export interface CustomerWalletOut {
  account_id: string;
  account_reference: string;
  currency: string;
  minor_units: number;
  available_cash: string;
  held_cash: string;
  available_bonus: string;
  held_bonus: string;
  version: number;
}

export interface PaymentIn {
  amount: number | string;
  provider?: string | null;
  external_reference?: string | null;
}

export interface WithdrawalIn {
  amount: number | string;
  provider?: string | null;
}

export interface PaymentOut {
  id: string;
  user_id: string;
  payment_type: string;
  status: string;
  amount: string;
  provider?: string | null;
  external_reference?: string | null;
  created_at: string;
  metadata_json: Record<string, unknown>;
}

export interface WalletTransactionsOut {
  payments: PaymentOut[];
  journals: Record<string, unknown>[];
}

// Preferences
export interface PreferenceOut {
  odds_format: string;
  locale: string;
  timezone: string;
  marketing_opt_in: boolean;
  payload?: Record<string, unknown>;
}

export interface PreferenceUpdateIn {
  odds_format?: string | null;
  locale?: string | null;
  timezone?: string | null;
  marketing_opt_in?: boolean | null;
  payload?: Record<string, unknown> | null;
}

// Session Bootstrap
export interface SessionBootstrapOut {
  user: UserOut;
  wallet: WalletOut;
  preferences: PreferenceOut;
  unread_notification_count: number;
}

// Notifications
export interface NotificationOut {
  id: string;
  kind: string;
  status: string;
  payload_json: Record<string, unknown>;
  created_at: string;
  read_at: string | null;
}

// Cashout
export interface CashoutQuoteIn {
  ticket_id: string;
}

export interface CashoutQuoteOut {
  id: string;
  ticket_id: string;
  amount: string;
  remaining_probability: string;
  haircut: string;
  expires_at: string;
}

// CustomBet / Bet Builder (see API docs section 6.6)
export interface CustomBetOutcome {
  outcome_id: string;
  name: string;
  odds: string;
  probability?: string;
  custombet_available: boolean;
  bettable: boolean;
  reason?: string | null;
}

export interface CustomBetMarket {
  market_id: string;
  specifiers?: Record<string, string>;
  specifiers_norm: string;
  canonical_key: string;
  name: string;
  custombet_available: boolean;
  outcomes: CustomBetOutcome[];
}

export interface CustomBetAvailableSelectionsOut {
  event_id: string;
  scope: string;
  custombet_available: boolean;
  reason?: string | null;
  expires_at?: string | null;
  markets: CustomBetMarket[];
  locale?: string;
  fallback_locale?: string;
}

export interface CustomBetFilterSelectionIn {
  market_id: string;
  specifiers?: string;
  outcome_id: string;
}

export interface CustomBetFilterSelectionOut {
  market_id: string;
  specifiers?: string;
  outcome_id: string;
  odds?: string;
  market_name?: string;
  name?: string;
  reason?: string;
}

export interface CustomBetFilterOut {
  event_id: string;
  scope: string;
  selected: CustomBetFilterSelectionOut[];
  accepted_odds?: string;
  probability?: string;
  harmonization?: boolean;
  available_selection_filter_complete?: boolean;
  compatible: CustomBetFilterSelectionOut[];
  incompatible: CustomBetFilterSelectionOut[];
}

// Top MatchKombis (homepage prebuilt same-event CustomBet suggestions; docs 4)
export interface TopMatchKombiSelection {
  event_id: string;
  market_id: string;
  specifiers: string;
  outcome_id: string;
  market_name: string;
  outcome_name: string;
}

export interface TopMatchKombiAddToBetslip {
  type: string;
  scope: string;
  event_id: string;
  selections: { market_id: string; specifiers: string; outcome_id: string }[];
}

export interface TopMatchKombiItem {
  rank: number;
  combination_id: string;
  type: string;
  event_id: string;
  event_name: string;
  scheduled_at?: string;
  sport_id?: string;
  category_id?: string;
  tournament_id?: string;
  combined_odds: string;
  selections: TopMatchKombiSelection[];
  add_to_betslip: TopMatchKombiAddToBetslip;
}

export interface TopMatchKombisOut {
  type: string;
  view: string;
  scope: string;
  sport_id?: string;
  locale?: string;
  fallback_locale?: string;
  limit: number;
  items: TopMatchKombiItem[];
}

// Top Combis (ready-made multi-event accumulator suggestions; docs 4)
export interface TopCombiSelection {
  event_id: string;
  event_name?: string;
  scheduled_at?: string;
  market_id: string;
  specifiers: string;
  outcome_id: string;
  market_name?: string;
  outcome_name?: string;
  odds?: string;
}

export interface TopCombiItem {
  combi_id: string;
  type: string;
  sport_id: string;
  sport_name?: string;
  fold_size: number;
  total_odds: string;
  source?: string;
  bets_placed_count?: number;
  bets_placed_count_is_observed?: boolean;
  selections_preview?: TopCombiSelection[] | null;
  selections?: TopCombiSelection[] | null;
  add_to_betslip: {
    type: string;
    legs: { event_id: string; market_id: string; specifiers: string; outcome_id: string }[];
  };
}

export interface TopCombiSportGroup {
  sport_id: string;
  sport_name: string;
  available_count: number;
  items: TopCombiItem[];
}

export interface TopCombisOut {
  type: string;
  pricing_mode?: string;
  quote_required?: boolean;
  locale?: string;
  fallback_locale?: string;
  folds?: string;
  source?: string;
  limit_per_sport?: number;
  sports: TopCombiSportGroup[];
}

// ── Casino ────────────────────────────────────────────────────────────────────

export interface CasinoProvider {
  provider_ref: string;
  name: string;
  category?: string;
}

export interface CasinoProviderListOut {
  casino_available: boolean;
  items: CasinoProvider[];
  next_cursor: string | null;
}

export interface CasinoGame {
  game_ref: string;
  title: string;
  category?: string;
  image_url?: string;
  devices: string[];
  currencies: string[];
  provider: CasinoProvider;
}

export interface CasinoGameListOut {
  casino_available: boolean;
  items: CasinoGame[];
  next_cursor: string | null;
}

export interface CasinoSessionIn {
  game_ref: string;
  language?: string;
  device?: 'desktop' | 'mobile';
}

export interface CasinoSessionOut {
  session_ref: string;
  launch_url: string;
}

// ── Cashback ──────────────────────────────────────────────────────────────────

export interface CashbackWagering {
  required: string;
  completed: string;
}

export interface CashbackProduct {
  product: 'sports' | 'casino';
  state: 'active' | 'upcoming' | 'disabled' | 'not_offered';
  available: boolean;
  currency: string;
  percentage: string;
  cadence: string;
  qualifying_loss: string | null;
  estimated_cashback: string | null;
  weekly_cap: string | null;
  weekly_cap_used: string | null;
  next_payment_at: string | null;
  wagering_multiplier: string | null;
  starts_at: string | null;
}

export interface CashbackSummaryOut {
  currency: string;
  products: CashbackProduct[];
  wagering: CashbackWagering | null;
}

export interface CashbackHistoryItem {
  product: 'sports' | 'casino';
  currency: string;
  cadence: string;
  period_start: string;
  period_end: string;
  qualifying_loss: string;
  percentage: string;
  weekly_cap: string;
  gross_amount: string;
  offset_amount: string;
  credited_amount: string;
  credited_at: string;
  wagering_requirement: string;
  wagering_progress: string;
  wagering_status: string;
}

export interface CashbackHistoryOut {
  items: CashbackHistoryItem[];
  next_cursor: string | null;
}


export interface ApiStructuredError {
  code: string;
  message_key: string;
  message: string;
  metadata?: Record<string, string>;
}

export class ApiException extends Error {
  public error?: ApiStructuredError;
  public detail?: string;
  public status?: number;

  constructor(msg: string, status?: number, errorObj?: ApiStructuredError, detail?: string) {
    super(msg);
    this.name = 'ApiException';
    this.status = status;
    this.error = errorObj;
    this.detail = detail;
  }
}
